import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { ArrowIcon } from "@/assets/icons";
import Button from "@/ui/Button/Button";
import {
  BloggerContestLeaderboard,
  BloggerContestParticipant,
  ContestRatingType,
} from "@/components/BloggerContestLeaderboard/BloggerContestLeaderboard";
import { getBloggerCompetition } from "@/api/base";
import { useUserInfoStore } from "@/store/userInfoStore";
import { useReferalsDataContext } from "@/store/ReferalsProvider";
import { BloggerCompetition } from "@/types/referals";
import ContainerLoader from "@/ui/ContainerLoader/ContainerLoader";
import { getDaysText } from "@/utils/helpers";
import leftDecoration from "@/assets/left.png";
import rightDecoration from "@/assets/right.png";

import cls from "./styles.module.scss";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });

const formatPeriod = (competition: BloggerCompetition) =>
  `С ${formatDate(competition.starts_at)} по ${formatDate(
    competition.registrations_end_at
  )}`;

const formatWaitingPeriod = (competition: BloggerCompetition) => {
  const startDate = new Date(competition.starts_at);
  const endDate = new Date(competition.registrations_end_at);
  const isSameMonth =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth();
  let competitionPeriod = `${formatDate(competition.starts_at)}–${formatDate(
    competition.registrations_end_at
  )}`;

  if (isSameMonth) {
    competitionPeriod = `${startDate.getDate()}–${endDate.getDate()} ${endDate.toLocaleDateString(
      "ru-RU",
      { month: "long" }
    )}`;
  }

  return `${competitionPeriod} · итоги ${formatDate(competition.results_at)}`;
};

const formatResultsPeriod = (competition: BloggerCompetition) => {
  const startDate = new Date(competition.starts_at);
  const endDate = new Date(competition.registrations_end_at);
  const isSameMonth =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth();

  if (isSameMonth) {
    const month = endDate.toLocaleDateString("ru-RU", { month: "long" });
    return `Конкурс шёл с ${startDate.getDate()} по ${endDate.getDate()} ${month}`;
  }

  return `Конкурс шёл с ${formatDate(competition.starts_at)} по ${formatDate(
    competition.registrations_end_at
  )}`;
};

const formatDaysLeft = (endDate: string) => {
  const millisecondsLeft = new Date(endDate).getTime() - Date.now();
  const days = Math.max(0, Math.ceil(millisecondsLeft / 86400000));

  return `Осталось ${getDaysText(days)}`;
};

const getCompetitionCopy = (competition: BloggerCompetition) => {
  switch (competition.state) {
    case "waiting_results":
      return {
        title: "Конкурс завершён,\nсчитаем просмотры",
        subtitle: formatWaitingPeriod(competition),
        status: "Конкурс завершён",
        ratingTitle: "Рейтинг блогеров",
        isFinished: true,
      };
    case "results":
      return {
        title: "Итоги подведены",
        subtitle: formatResultsPeriod(competition),
        status: "Финальный рейтинг",
        ratingTitle: "Итоговый рейтинг",
        isFinished: true,
      };
    case "active":
    default:
      return {
        title: "Делай просмотры, приводи рефералов, забирай главный приз!",
        subtitle: formatPeriod(competition),
        status: formatDaysLeft(competition.registrations_end_at),
        ratingTitle: "Рейтинг блогеров",
        isFinished: false,
      };
  }
};

const findRegistrationScore = (
  competition: BloggerCompetition,
  participantId: number
) => {
  const participant = competition.registrations.items.find(
    (item) => item.participant_id === participantId
  );
  if (participant) return participant.registrations;
  if (competition.registrations.me?.participant_id === participantId) {
    return competition.registrations.me.registrations;
  }
  return null;
};

const findViewsScore = (
  competition: BloggerCompetition,
  participantId: number
) => {
  const participant = competition.views.items.find(
    (item) => item.participant_id === participantId
  );
  if (participant) return participant.views;
  if (competition.views.me?.participant_id === participantId) {
    return competition.views.me.views;
  }
  return null;
};

const buildLeaderboard = (
  competition: BloggerCompetition | null,
  ratingType: ContestRatingType
): {
  participants: BloggerContestParticipant[];
  me: BloggerContestParticipant | null;
} => {
  if (!competition) return { participants: [], me: null };

  if (ratingType === "views") {
    const participants = competition.views.items.map((participant) => ({
      participantId: participant.participant_id,
      displayName: participant.display_name,
      place: participant.place,
      score: participant.views,
      secondaryScore: findRegistrationScore(
        competition,
        participant.participant_id
      ),
    }));
    let me: BloggerContestParticipant | null = null;

    if (competition.views.me) {
      me = {
        participantId: competition.views.me.participant_id,
        displayName: "Ты",
        place: competition.views.me.place,
        score: competition.views.me.views,
        secondaryScore: competition.registrations.me?.registrations ?? null,
      };
    }

    return { participants, me };
  }

  const participants = competition.registrations.items.map((participant) => ({
    participantId: participant.participant_id,
    displayName: participant.display_name,
    place: participant.place,
    score: participant.registrations,
    secondaryScore: findViewsScore(competition, participant.participant_id),
  }));
  let me: BloggerContestParticipant | null = null;

  if (competition.registrations.me) {
    me = {
      participantId: competition.registrations.me.participant_id,
      displayName: "Ты",
      place: competition.registrations.me.place,
      score: competition.registrations.me.registrations,
      secondaryScore: competition.views.me?.views ?? null,
    };
  }

  return { participants, me };
};

export const BloggerContestPage = () => {
  const navigate = useNavigate();
  const [ratingType, setRatingType] = useState<ContestRatingType>("views");
  const [competition, setCompetition] = useState<BloggerCompetition | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const yandexId = useUserInfoStore((state) => state.yandexId);
  const serviceType = useUserInfoStore((state) => state.serviceType);
  const { userStatus } = useReferalsDataContext();
  const canAccessCompetition =
    userStatus?.capabilities.blogger_competition === true;

  useEffect(() => {
    if (!yandexId || !serviceType || !canAccessCompetition) return;

    let isCancelled = false;
    setLoading(true);
    setHasError(false);

    getBloggerCompetition(yandexId, serviceType)
      .then((response) => {
        if (!isCancelled) setCompetition(response.competition);
      })
      .catch(() => {
        if (!isCancelled) setHasError(true);
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [yandexId, serviceType, canAccessCompetition]);

  const leaderboard = buildLeaderboard(competition, ratingType);
  const competitionCopy = competition ? getCompetitionCopy(competition) : null;

  return (
    <main className={cls.page}>
      <section className={cls.hero}>
        <div className={cls.header}>
          <Button
            trigger="ref.contest.back"
            variant="iconOnly"
            className={cls.backButton}
            onClick={() => navigate(-1)}
            ariaLabel="Назад"
            icon={<ArrowIcon />}
          />
          <strong>Конкурс блогеров</strong>
          <span />
        </div>

        {competition && (
          <>
            <img
              className={cls.decoration}
              src={rightDecoration}
              alt=""
              aria-hidden="true"
            />
            <img
              className={cls.thumbsup}
              src={leftDecoration}
              alt=""
              aria-hidden="true"
            />
            <div className={cls.heroContent}>
              <h1>{competitionCopy?.title}</h1>
              <p>{competitionCopy?.subtitle}</p>
              <span
                className={classNames(
                  cls.status,
                  competitionCopy?.isFinished && cls.finishedStatus
                )}
              >
                {competitionCopy?.status}
              </span>
            </div>
          </>
        )}
      </section>

      {loading && canAccessCompetition && (
        <div className={cls.loading}>
          <ContainerLoader />
        </div>
      )}

      {!loading && hasError && (
        <section className={cls.empty}>
          <h2>Не удалось загрузить конкурс</h2>
          <p>Попробуй открыть страницу ещё раз</p>
        </section>
      )}

      {userStatus && !canAccessCompetition && (
        <section className={cls.empty}>
          <h2>Конкурс недоступен</h2>
        </section>
      )}

      {!loading && !hasError && canAccessCompetition && !competition && (
        <section className={cls.empty}>
          <h2>Сейчас конкурсов нет</h2>
          <p>Новый объявим в приложении</p>
        </section>
      )}

      {!loading && competition && (
        <section className={cls.rating}>
          <h2>{competitionCopy?.ratingTitle}</h2>

          <div className={cls.tabs} role="tablist" aria-label="Тип рейтинга">
            <button
              type="button"
              data-trigger="ref.contest.views"
              role="tab"
              aria-selected={ratingType === "views"}
              className={classNames(ratingType === "views" && cls.activeTab)}
              onClick={() => setRatingType("views")}
            >
              По просмотрам
            </button>
            <button
              type="button"
              data-trigger="ref.contest.registrations"
              role="tab"
              aria-selected={ratingType === "registrations"}
              className={classNames(
                ratingType === "registrations" && cls.activeTab
              )}
              onClick={() => setRatingType("registrations")}
            >
              По регистрациям
            </button>
          </div>

          <BloggerContestLeaderboard
            participants={leaderboard.participants}
            me={leaderboard.me}
            ratingType={ratingType}
          />
        </section>
      )}
    </main>
  );
};
